import type { LocationQuery, LocationQueryRaw } from "vue-router";
import type { VideoSort } from "@/api";

// Two galleries over the same list (collection-discover): Collection, what
// the libraries hold, and Discover, what is out there to get. Each keeps
// its filters in the URL and remembers them (per browser) for the next
// visit, so the nav and the way back from a video land where it was left.

export type GalleryMode = "collection" | "discover";

export const GALLERY_ROUTE: Record<GalleryMode, string> = { collection: "home", discover: "discover" };

export interface GalleryFilters {
    q: string;
    tag: string | null;
    actress: string | null;
    studio: string | null;
    status: string;
    year: number | null;
    month: number | null;
    rating: number | null;
    missingMetadata: boolean;
    neverPlayed: boolean;
    hideHated: boolean;
    sort: VideoSort;
    page: number;
}

export const GALLERY_DEFAULTS: Record<GalleryMode, GalleryFilters> = {
    collection: {
        q: "", tag: null, actress: null, studio: null, status: "owned", year: null, month: null, rating: null,
        missingMetadata: false, neverPlayed: false, hideHated: false, sort: "newest", page: 1,
    },
    discover: {
        q: "", tag: null, actress: null, studio: null, status: "new,wishlist", year: null, month: null, rating: null,
        missingMetadata: false, neverPlayed: false, hideHated: true, sort: "release", page: 1,
    },
};

/** Discover's status choices; Collection is owned only. */
export const DISCOVER_STATUSES: Array<{ value: string; label: string; icon: string }> = [
    { value: "new,wishlist", label: "To get", icon: "pi pi-bolt" },
    { value: "new", label: "New", icon: "pi pi-sparkles" },
    { value: "wishlist", label: "Wishlist", icon: "pi pi-bookmark" },
    { value: "ignored", label: "Ignored", icon: "pi pi-eye-slash" },
    { value: "new,wishlist,ignored", label: "All not owned", icon: "pi pi-list" },
];

const SORTS: VideoSort[] = ["newest", "release", "rating", "title", "most_played", "recently_played"];

const str = (v: unknown) => (typeof v === "string" ? v : undefined);
const num = (v: unknown) => {
    const n = Number(str(v));
    return Number.isFinite(n) && n > 0 ? n : undefined;
};
const bool = (v: unknown) => (str(v) === undefined ? undefined : str(v) === "1");

/** The filters a URL asks for; what it leaves out is the gallery's default. */
export function filtersFromQuery(query: LocationQuery, mode: GalleryMode): GalleryFilters {
    const d = GALLERY_DEFAULTS[mode];
    const sort = str(query.sort) as VideoSort | undefined;
    const status = mode === "collection" ? d.status : str(query.status);
    return {
        q: str(query.q) ?? d.q,
        tag: str(query.tag) ?? d.tag,
        actress: str(query.actress) ?? d.actress,
        studio: str(query.studio)?.toUpperCase() ?? d.studio,
        status: status && DISCOVER_STATUSES.some((s) => s.value === status) ? status : d.status,
        year: num(query.year) ?? d.year,
        month: num(query.month) ?? d.month,
        rating: num(query.rating) ?? d.rating,
        missingMetadata: bool(query.missing) ?? d.missingMetadata,
        neverPlayed: bool(query.unplayed) ?? d.neverPlayed,
        hideHated: bool(query.hide_hated) ?? d.hideHated,
        sort: sort && SORTS.includes(sort) ? sort : d.sort,
        page: num(query.page) ?? d.page,
    };
}

/** The URL of the filters: only what differs from the gallery's default. */
export function queryFromFilters(f: GalleryFilters, mode: GalleryMode): LocationQueryRaw {
    const d = GALLERY_DEFAULTS[mode];
    const q: LocationQueryRaw = {};
    if (f.q !== d.q) q.q = f.q;
    if (f.tag !== d.tag) q.tag = f.tag;
    if (f.actress !== d.actress) q.actress = f.actress;
    if (f.studio !== d.studio) q.studio = f.studio;
    if (mode === "discover" && f.status !== d.status) q.status = f.status;
    if (f.year !== d.year) q.year = String(f.year);
    if (f.month !== d.month) q.month = String(f.month);
    if (f.rating !== d.rating) q.rating = String(f.rating);
    if (f.missingMetadata !== d.missingMetadata) q.missing = f.missingMetadata ? "1" : "0";
    if (f.neverPlayed !== d.neverPlayed) q.unplayed = f.neverPlayed ? "1" : "0";
    if (f.hideHated !== d.hideHated) q.hide_hated = f.hideHated ? "1" : "0";
    if (f.sort !== d.sort) q.sort = f.sort;
    if (f.page !== d.page) q.page = String(f.page);
    return q;
}

export function sameFilters(a: GalleryFilters, b: GalleryFilters): boolean {
    return (Object.keys(a) as Array<keyof GalleryFilters>).every((k) => a[k] === b[k]);
}

const storageKey = (mode: GalleryMode) => `ahegao.gallery.${mode}`;

/** The query a gallery was left with in this browser, if any. */
export function savedQuery(mode: GalleryMode): LocationQueryRaw | null {
    try {
        const raw = localStorage.getItem(storageKey(mode));
        return raw ? (JSON.parse(raw) as LocationQueryRaw) : null;
    } catch {
        return null;
    }
}

export function saveQuery(mode: GalleryMode, query: LocationQueryRaw) {
    try {
        localStorage.setItem(storageKey(mode), JSON.stringify(query));
    } catch {
        // private window: the URL still holds it
    }
}
